import {
  LogoGallery as LogoGalleryUI,
  ILogoGalleryItem,
} from "@business-finland/wif-ui-lib/dist/LogoGallery";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { Viewport } from "../../../constants/app";
import useImageDataHandler, {
  ImageDataHandlerFn,
} from "../../../hooks/useImageDataHandler";
import { LinkSwitchableField } from "../../../types/wif-next";
import {
  jcrMultiFieldToArray,
  resolveLinkSwitchableField,
} from "../../../util/magnolia";

interface ILogoItemNode {
  image: Wif.Mgnl.Image;
  altText?: string;
  link?: LinkSwitchableField;
}

interface ILogoGalleryProps {
  header: string;
  leadText?: string;
  logos: Wif.Mgnl.NodeWithList<Wif.Mgnl.Node<ILogoItemNode>>;
}

export function LogoGallery(props: ILogoGalleryProps): JSX.Element | null {
  const { isPreview } = useRouter();
  const imageDataHandler = useImageDataHandler();
  const logos = useMemo(
    () =>
      jcrMultiFieldToArray(props.logos).map((node) =>
        mapLogoNodeToItem(node, imageDataHandler, isPreview)
      ),
    [props.logos, imageDataHandler, isPreview]
  );

  if (logos.length === 0) return null;

  return (
    <LogoGalleryUI
      header={props.header}
      leadText={props.leadText}
      logos={logos}
    />
  );
}

function mapLogoNodeToItem(
  node: Wif.Mgnl.Node<ILogoItemNode>,
  imageDataHandler: ImageDataHandlerFn,
  isPreview: boolean
): ILogoGalleryItem {
  const { image, altText, link } = node;
  const imageData = imageDataHandler(image, { maxViewport: Viewport.sm });
  const { linkHref, isExternalLink } = resolveLinkSwitchableField(
    link,
    isPreview
  );

  return {
    image: {
      ...imageData,
      altText: altText || image.metadata?.title || "Company logo",
    },
    href: linkHref || undefined,
    isExternalLink,
  };
}
